/**
 * The Receipt section's rows: one per paid contract (T-160).
 *
 * The backend renders one receipt per contract with a completed payment and
 * stores each under its own path (`<funeral>/receipts/cleo-contract-receipt-<contract>.pdf`).
 * A pre-need that came to fruition carries two paid contracts — its pre-need
 * agreement and the at-need contract (case 743: 605 + 752) — so each gets a
 * row with its own Download or Generate. A case with one paid contract keeps
 * the single "Cleo Contract Receipt" row it always had.
 */

export interface ReceiptContract {
  id: number | string;
  is_preneed?: boolean | null;
}

export interface ReceiptPayment {
  contract_id?: number | string | null;
  payment_status?: string | null;
  transaction_type?: string | null;
}

export interface ReceiptDocument {
  document_type: string;
  contract_id?: number | null;
  received_at: string;
}

export interface ReceiptRow<D extends ReceiptDocument> {
  key: string;
  label: string;
  /** The contract Generate renders for; null when none can be resolved. */
  contractId: number | null;
  document: D | null;
}

const BASE_LABEL = 'Cleo Contract Receipt';

// Same rule as the backend's receipt render (contract.service.ts).
const isCompletedPayment = (p: ReceiptPayment) =>
  (p.payment_status === 'completed' || p.payment_status === 'paid') &&
  p.transaction_type === 'payment';

export function receiptRows<D extends ReceiptDocument>(
  contracts: ReceiptContract[],
  payments: ReceiptPayment[],
  receiptDocs: D[],
  /** Contract to Generate for when no paid contract is known (the old single-row fallback). */
  fallbackContractId?: number,
): ReceiptRow<D>[] {
  const paidIds = new Set(payments.filter(isCompletedPayment).map(p => Number(p.contract_id)));
  const paidContracts = contracts
    .filter(c => paidIds.has(Number(c.id)))
    .sort((a, b) => Number(a.id) - Number(b.id));
  const paidContractIds = new Set(paidContracts.map(c => Number(c.id)));
  // Newest first, so a contract's row shows its latest stored receipt.
  const docs = [...receiptDocs].sort(
    (a, b) => new Date(b.received_at).getTime() - new Date(a.received_at).getTime(),
  );

  const rows: Array<Omit<ReceiptRow<D>, 'label'> & { contract?: ReceiptContract }> = [
    ...paidContracts.map(contract => ({
      key: `contract-${contract.id}`,
      contract,
      contractId: Number(contract.id),
      document: docs.find(d => Number(d.contract_id) === Number(contract.id)) ?? null,
    })),
    // A stored receipt whose contract no longer shows as paid (or that has no
    // contract) still gets listed rather than disappearing.
    ...docs
      .filter(d => d.contract_id == null || !paidContractIds.has(Number(d.contract_id)))
      .filter((d, i, all) =>
        d.contract_id == null || all.findIndex(o => Number(o.contract_id) === Number(d.contract_id)) === i,
      )
      .map((d, i) => ({
        key: `doc-${d.contract_id ?? `none-${i}`}`,
        contract: contracts.find(c => Number(c.id) === Number(d.contract_id)),
        contractId: d.contract_id ?? null,
        document: d,
      })),
  ];

  if (rows.length === 0) {
    return [{ key: 'receipt', label: BASE_LABEL, contractId: fallbackContractId ?? null, document: null }];
  }
  if (rows.length === 1) {
    const [row] = rows;
    return [{
      key: row.key,
      label: BASE_LABEL,
      contractId: row.contractId ?? fallbackContractId ?? null,
      document: row.document,
    }];
  }
  return rows.map(row => ({
    key: row.key,
    label: rowLabel(row.contract, row.contractId, row.document),
    contractId: row.contractId,
    document: row.document,
  }));
}

function rowLabel(
  contract: ReceiptContract | undefined,
  contractId: number | null,
  document: ReceiptDocument | null,
): string {
  if (contractId == null) {
    const date = document
      ? new Date(document.received_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : '';
    return date ? `${BASE_LABEL} (${date})` : BASE_LABEL;
  }
  if (!contract) return `${BASE_LABEL} #${contractId}`;
  return `${BASE_LABEL} — ${contract.is_preneed ? 'Pre-need' : 'At-need'} #${contractId}`;
}
